'use client';

import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';

import { BuyTicketFormValues } from '@/components/Form/MultiStepForm/BuyTicketForm.type';
import { useMultiStepFormState } from '@/components/Form/MultiStepForm/MultiStepFormContext';
import withFormProvider from '@/components/Hoc/WithMultiStepFormProvider';
import Badge from '@/components/UI/Badge';
import DateBadge from '@/components/UI/DateBadge';
import { Event } from '@/types/event';
import { EventManagerQuestion } from '@/types/eventManagerQuestion';
import { EventTicket } from '@/types/eventTicket';
import { EventTicketType } from '@/types/eventTicketType';
import { PlaceMusicCategory } from '@/types/placeMusicCategory';

import EventPageBuyTicketSection from './EventPageBuyTicketSection';
import EventPageDescriptionSection from './EventPageDescriptionSection';
import EventPageHeroSection from './EventPageHeroSection';
import EventPagePlaceSection from './EventPagePlaceSection';
import EventPageSelectTicketSection from './EventPageSelectTicketSection';

interface EventPageWrapperProps {
  event: Event;
  place: any;
  musics: PlaceMusicCategory[];
  coverUrl?: string;
  userToken?: string;
  eventTickets: EventTicket[] | null;
  eventManagerQuestions: EventManagerQuestion[];
  eventTicketTypes: EventTicketType[];
  contactEmail: string;
}

function EventPage({
  event,
  musics,
  place,
  coverUrl,
  userToken,
  eventTickets,
  eventManagerQuestions,
  eventTicketTypes,
  contactEmail,
}: EventPageWrapperProps) {
  const router = useRouter();
  useEffect(() => {
    router.refresh();
  }, [event.id]);

  const [firstLoad, setFirstLoad] = useState(true);
  const { formData, a, updateFormData } =
    useMultiStepFormState<BuyTicketFormValues>();

  useEffect(() => {
    if (firstLoad) {
      setFirstLoad(false);
      if (formData?.eventTicketTypes) {
        updateFormData((data) => {
          return {
            ...data,
            eventTicketTypes: {},
          } as BuyTicketFormValues;
        });
      }
    }
  }, [formData]);

  const totalQuantity = useMemo(() => {
    if (!formData?.eventTicketTypes) return 0;
    return Object.values(formData.eventTicketTypes).reduce(
      (acc, item) => acc + (item?.quantity || 0),
      0
    );
  }, [formData, a]);
  if (firstLoad) return null;
  return (
    <div className='mx-auto grid max-w-6xl pt-10 sm:grid-cols-2'>
      {/* Left side Desktop - Bottom Mobile */}
      <div className='order-2 px-6 sm:order-1 sm:px-3'>
        <EventPageHeroSection
          event={event}
          place={place}
          musics={musics}
          className='order-2 sm:order-1'
        />

        {/* {eventTicketsPurchase && eventTicketsPurchase.length > 0 && (
          <PlacePageEventTicketsViewTicketsCTA
            eventId={event.id}
            placeId={place.id}
            ticketCount={alreadyBoughtTickets}
            className='flex justify-center space-x-2 px-2'
          />
        )} */}

        <EventPageSelectTicketSection
          event={event}
          placeId={place.id}
          eventTicketTypes={eventTicketTypes}
          coverUrl={coverUrl}
          userToken={userToken}
        />

        <EventPageDescriptionSection
          description={event.description}
          className='order-3'
        />

        <EventPagePlaceSection
          place={place}
          contactEmail={contactEmail}
          className='order-4'
        />
      </div>
      {/* Right side Desktop - Top Mobile */}
      <div className='order-1  px-3 pb-7 sm:order-2 '>
        <div className='space-y-10 sm:sticky sm:top-10'>
          {coverUrl && (
            <div className='relative order-1 mx-auto flex aspect-video h-full w-full items-center overflow-hidden rounded-2xl sm:order-2 sm:h-80'>
              {event.entryType === 'private' && (
                <div className='absolute left-3 top-3 z-10'>
                  <Badge type='private' />
                </div>
              )}
              <div className='absolute right-5 top-5 z-10'>
                <DateBadge date={event.dateFrom} />
              </div>
              <Image
                layout='fill'
                src={coverUrl}
                alt='cover'
                className='object-cover object-center'
                priority
              />
            </div>
          )}
          {!!(totalQuantity && totalQuantity > 0) && (
            <div className=''>
              <EventPageBuyTicketSection
                ticketCount={totalQuantity}
                placeId={place.id}
                userToken={userToken}
                eventManagerQuestions={eventManagerQuestions}
                eventTicketTypes={eventTicketTypes}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default withFormProvider<BuyTicketFormValues>(
  EventPage,
  'outapp-buy-ticket'
);
